// helpers/warnRequired.js
let __missingStylesInjected = false;
const injectGlobalStyle = () => {
  if (__missingStylesInjected) return;
  __missingStylesInjected = true;
  const style = document.createElement('style');
  style.id = 'missing-field-styles';
  style.textContent = `
    .is-missing { outline: 2px solid rgba(239,83,80,0.45); box-shadow: 0 0 0 2px rgba(239,83,80,0.12) inset; }
    .js-missing-label { color: #ef5350; }
    .field-missing { margin-bottom: 18px; }
  `;
  document.head.appendChild(style);
};

const PLACEHOLDER_CLASS = 'Input_placeholder__cmJqY';
const MISSING_LABEL_MARK = 'js-missing-label';

const humanize = (s = '') =>
  String(s).replace(/[_-]+/g, ' ')
           .replace(/([a-z])([A-Z])/g, '$1 $2')
           .replace(/\s+/g, ' ')
           .trim()
           .replace(/\b(external )?id\b$/i, '')
           .trim()
           .replace(/^./, c => c.toUpperCase());

const isSelect = (el) => el?.tagName?.toLowerCase() === 'select';
const isDisabled = (el) => el.disabled || el.getAttribute('aria-disabled') === 'true';

const getValue = (el) => {
  const dv = el.getAttribute?.('data-value');
  if (dv != null) return String(dv).trim();
  return String(el.value ?? '').trim();
};

const isPlaceholderOption = (opt) => {
  if (!opt) return true;
  if (opt.disabled || opt.hidden) return true;
  const v = String(opt.value ?? '').trim();
  if (v === '') return true;
  if (opt.getAttribute?.('data-placeholder') === 'true') return true;
  return false;
};

const isEmpty = (el) => {
  if (isDisabled(el)) return false;

  if (isSelect(el)) {
    const v = getValue(el);
    // Prefer value (React may set value before selectedIndex updates)
    if (v && !['default','select','choose','undefined','null'].includes(v)) return false;
    const idx = el.selectedIndex ?? -1;
    if (idx < 0) return true;
    return isPlaceholderOption(el.options[idx]);
  }

  const v = getValue(el).toLowerCase();
  return v === '' || v === 'default' || v === 'select' || v === 'choose' || v === 'undefined' || v === 'null';
};

const labelOf = (el) =>
  el.getAttribute('data-label') ||
  el.getAttribute('placeholder') ||
  el.getAttribute('aria-label') ||
  humanize(el.getAttribute('name') || el.getAttribute('id') || 'Field');

const keyOf = (el) => el.getAttribute('name') || el.getAttribute('id') || labelOf(el);

const containerOf = (el) =>
  el.closest?.('[data-field-container]') ||
  el.closest?.('[class*="Input_container__"]') ||
  el.closest?.('[class*="Select_container__"]') ||
  el.parentElement;

// collect both "required-marked" nodes AND any nodes inside containers that already show an error span
const collectNodes = (root) => {
  const required = root.querySelectorAll?.([
    '[data-required="true"]',
    '[aria-required="true"]',
    'input[required]',
    'select[required]',
    'textarea[required]',
    'input[class*="required"]',
    'select[class*="required"]',
    'textarea[class*="required"]',
  ].join(', ')) || [];

  const withLabels = root.querySelectorAll?.(`.${MISSING_LABEL_MARK}`) || [];
  const extra = [];
  withLabels.forEach(span => {
    const box = span.closest?.('[data-field-container], [class*="Input_container__"], [class*="Select_container__"]');
    if (!box) return;
    const el = box.querySelector('input, select, textarea');
    if (el) extra.push(el);
  });

  // unique list
  const set = new Set();
  [...required, ...extra].forEach(n => set.add(n));
  return Array.from(set);
};

// sweep orphan labels before/after evaluation
const sweepOrphans = (root) => {
  const labels = root.querySelectorAll?.(`.${MISSING_LABEL_MARK}`) || [];
  labels.forEach(span => {
    const box = span.closest?.('[data-field-container], [class*="Input_container__"], [class*="Select_container__"]');
    const el = box?.querySelector?.('input, select, textarea');
    if (!el) { span.remove(); return; }
    if (!isEmpty(el)) span.remove();
  });
};

const evaluate = (root, nodes) => {
  const missingKeys = new Set();

  // pre-sweep to cleanup obvious orphans
  sweepOrphans(root);

  nodes.forEach((el) => {
    const empty = isEmpty(el);
    const key = keyOf(el);
    const label = labelOf(el);
    const box = containerOf(el);

    el.classList.toggle('is-missing', empty);
    el.setAttribute('aria-invalid', empty ? 'true' : 'false');

    if (box) {
      box.classList.toggle('field-missing', empty);
      let span = box.querySelector(`.${MISSING_LABEL_MARK}`);
      if (empty) {
        if (!span) {
          span = document.createElement('span');
          span.className = `${PLACEHOLDER_CLASS} ${MISSING_LABEL_MARK}`;
          box.insertBefore(span, box.firstChild);
        }
        span.textContent = `${label} is required`;
      } else if (span) {
        span.remove();
      }
    }

    if (empty) missingKeys.add(key);
    else missingKeys.delete(key);
  });

  // post-sweep in case containers changed
  sweepOrphans(root);

  return Array.from(missingKeys).map(humanize);
};

export const findMissingRequired = (container) => {
  injectGlobalStyle();
  const root = container?.current ?? container ?? null;
  if (!root) return [];

  const nodes = collectNodes(root);

  nodes.forEach((el) => {
    if (el.__reqBound) return;
    const refresh = () => {
      const local = new Set([el]);
      const box = containerOf(el);
      if (box) collectNodes(box).forEach(n => local.add(n));
      evaluate(root, Array.from(local));
    };
    el.addEventListener('input', refresh);
    el.addEventListener('change', refresh);
    el.addEventListener('blur', refresh);
    el.__reqBound = true;
  });

  const missing = evaluate(root, nodes);

  // also nudge once after paint to catch async hydration
  requestAnimationFrame(() => evaluate(root, collectNodes(root)));
  setTimeout(() => evaluate(root, collectNodes(root)), 120);

  return missing;
};
